import { LIST_OPERATORS, RSQL_OPERATORS, } from '#src/rsql/ast.js';
export class RsqlSyntaxError extends Error {
    position;
    constructor(message, position) {
        super(`posição ${position + 1}: ${message}`);
        this.position = position;
        this.name = 'RsqlSyntaxError';
    }
}
const SELECTOR = /[A-Za-z][A-Za-z0-9_]*(?:\.[A-Za-z][A-Za-z0-9_]*)*/y;
const FIQL_OPERATOR = /=[a-z]*=/y;
/** Caracteres que encerram um valor sem aspas. */
const RESERVED = new Set([
    '"',
    "'",
    '(',
    ')',
    ';',
    ',',
    '=',
    '!',
    '~',
    '<',
    '>',
]);
/** Formas alternativas que outras implementações aceitam e esta recusa. */
const ALIASES = {
    '<': '=lt=',
    '<=': '=le=',
    '>': '=gt=',
    '>=': '=ge=',
};
const isOperator = (text) => RSQL_OPERATORS.includes(text);
/**
 * Parser de RSQL estrito, por descida recursiva:
 *
 *   or         = and ("," and)*
 *   and        = constraint (";" constraint)*
 *   constraint = "(" or ")" | comparison
 *   comparison = selector operator arguments
 *   arguments  = "(" value ("," value)* ")"   (só =in= e =out=)
 *              | value
 *   value      = sem-aspas | "entre aspas" | 'entre aspas'
 *
 * `;` (e) tem precedência sobre `,` (ou). Espaços só valem dentro de aspas.
 */
class Parser {
    text;
    limits;
    position = 0;
    depth = 0;
    comparisons = 0;
    constructor(text, limits) {
        this.text = text;
        this.limits = limits;
    }
    parse() {
        const node = this.or();
        if (this.position < this.text.length) {
            if (/^\s+(and|or)\s/i.test(this.text.slice(this.position))) {
                this.fail('use ";" para "e" e "," para "ou"; and/or não são aceitos');
            }
            this.fail(`esperava ";", "," ou o fim, achou "${this.peek()}"`);
        }
        return node;
    }
    or() {
        return this.logical('or', ',', () => this.and());
    }
    and() {
        return this.logical('and', ';', () => this.constraint());
    }
    logical(type, separator, next) {
        const children = [next()];
        while (this.peek() === separator) {
            this.position += 1;
            children.push(next());
        }
        return children.length === 1 && children[0] !== undefined
            ? children[0]
            : { type, children };
    }
    constraint() {
        if (this.peek() !== '(') {
            return this.comparison();
        }
        this.depth += 1;
        if (this.depth > this.limits.maxDepth) {
            this.fail(`mais de ${this.limits.maxDepth} níveis de parênteses`);
        }
        this.position += 1;
        const node = this.or();
        this.expect(')');
        this.depth -= 1;
        return node;
    }
    comparison() {
        const position = this.position;
        const selector = this.selector();
        const operator = this.operator();
        const values = LIST_OPERATORS.includes(operator)
            ? this.list()
            : [this.value()];
        this.comparisons += 1;
        if (this.comparisons > this.limits.maxComparisons) {
            this.fail(`mais de ${this.limits.maxComparisons} comparações`, position);
        }
        return { type: 'comparison', selector, operator, values, position };
    }
    selector() {
        SELECTOR.lastIndex = this.position;
        const match = SELECTOR.exec(this.text);
        if (match === null) {
            this.fail('esperava o nome de um campo');
        }
        this.position = SELECTOR.lastIndex;
        return match[0];
    }
    operator() {
        const two = this.text.slice(this.position, this.position + 2);
        if (two === '==' || two === '!=') {
            this.position += 2;
            return two;
        }
        const written = ALIASES[two] === undefined ? two.charAt(0) : two;
        const alias = ALIASES[written];
        if (alias !== undefined) {
            this.fail(`operador "${written}" não é aceito; use ${alias}`);
        }
        FIQL_OPERATOR.lastIndex = this.position;
        const match = FIQL_OPERATOR.exec(this.text);
        if (match === null || !isOperator(match[0])) {
            this.fail(`operador desconhecido; use ${RSQL_OPERATORS.join(' ')}${match === null ? '' : ` (achou "${match[0]}")`}`);
        }
        this.position = FIQL_OPERATOR.lastIndex;
        return match[0];
    }
    list() {
        this.expect('(');
        const values = [this.value()];
        while (this.peek() === ',') {
            this.position += 1;
            values.push(this.value());
        }
        this.expect(')');
        if (values.length > this.limits.maxListValues) {
            this.fail(`mais de ${this.limits.maxListValues} valores na lista`);
        }
        return values;
    }
    value() {
        const quote = this.peek();
        const value = quote === '"' || quote === "'" ? this.quoted(quote) : this.unquoted();
        if (value.length > this.limits.maxValueLength) {
            this.fail(`valor com mais de ${this.limits.maxValueLength} caracteres`);
        }
        return value;
    }
    unquoted() {
        const start = this.position;
        while (this.position < this.text.length) {
            const char = this.text.charAt(this.position);
            if (RESERVED.has(char) || /\s/.test(char)) {
                break;
            }
            this.position += 1;
        }
        if (this.position === start) {
            this.fail('esperava um valor; use aspas para espaços ou símbolos');
        }
        return this.text.slice(start, this.position);
    }
    /** Dentro de aspas, `\` escapa o próximo caractere (aspas ou `\`). */
    quoted(quote) {
        const start = this.position;
        this.position += 1;
        let value = '';
        while (this.position < this.text.length) {
            const char = this.text.charAt(this.position);
            if (char === quote) {
                this.position += 1;
                return value;
            }
            if (char === '\\') {
                this.position += 1;
            }
            value += this.text.charAt(this.position);
            this.position += 1;
        }
        return this.fail('aspas sem fechamento', start);
    }
    peek() {
        return this.text.charAt(this.position);
    }
    expect(char) {
        if (this.peek() !== char) {
            this.fail(`esperava "${char}"`);
        }
        this.position += 1;
    }
    fail(message, position = this.position) {
        throw new RsqlSyntaxError(message, position);
    }
}
/** @throws RsqlSyntaxError */
export function parseRsql(text, limits) {
    if (text.length > limits.maxFilterLength) {
        throw new RsqlSyntaxError(`filtro com mais de ${limits.maxFilterLength} caracteres`, limits.maxFilterLength);
    }
    if (text.length === 0) {
        throw new RsqlSyntaxError('filtro vazio', 0);
    }
    return new Parser(text, limits).parse();
}
//# sourceMappingURL=parser.js.map